import { useCallback, useEffect, useRef, useState } from 'react'

import * as S from './styled'
import { acquireSlot, releaseSlot } from './loadQueue'

// 일시적 실패(네트워크 블립·동시 요청 폭주)는 1회 재요청으로 대부분 복구된다.
const MAX_RETRY = 1

// 뷰포트에 이만큼 가까워지면 미리 로드 시작(스크롤 체감 개선).
const PREFETCH_MARGIN = '200px'

// 슬롯 점유 상한. 이 시간이 지나면 "이미지를 실패 처리"하는 게 아니라 슬롯만 반납해
// 대기 중인 다음 썸네일이 진행되게 한다(큐 기아 방지). 느린 이미지는 그대로 계속 로딩된다.
const SLOT_HOLD_MS = 6000

const isEmptySrc = (src) => !src || (typeof src === 'string' && src.trim() === '')

// 재요청 시 캐시버스트 쿼리를 붙여 브라우저·DOM이 확실히 다시 fetch 하도록 한다.
// 값을 1로 고정해 재시도 캐시키가 매번 동일 → nginx 캐시는 게임당 최대 2개 엔트리로 제한된다.
const withRetryParam = (src) => {
  if (!src) return src
  const sep = src.includes('?') ? '&' : '?'
  return `${src}${sep}_r=1`
}

const initialPhase = (src, priority) => {
  if (isEmptySrc(src)) return 'error'
  return priority ? 'slot' : 'view'
}

/**
 * 게임 썸네일 (순차 렌더링 · 취소 안전)
 * - phase: view(뷰포트 대기) → slot(로드 슬롯 대기) → loading(fetch) → loaded / error
 * - 뷰포트 진입 시에만 로드하고, 전역 동시성 큐로 N개씩 순차 로드한다(저속 회선 대비).
 * - loading부터는 <img>를 계속 마운트 유지하고 스켈레톤은 위에 덮는 오버레이로 처리한다.
 *   → 요소 교체로 인한 다운로드 취소가 없다.
 * - 각 단계가 phase 기반 개별 effect라, 어떤 상태로 돌아가도 로드가 다시 걸린다(기아/stranding 방지).
 * - 시간 기반으로 이미지를 실패 처리하지 않는다(느릴 뿐인 이미지를 죽이지 않음). onError에만 반응.
 *
 * @param {Object} props
 * @param {string} props.src - 썸네일 URL
 * @param {string} props.alt - 게임 제목 (fallback 시에도 노출)
 * @param {boolean} [props.priority] - true면 뷰포트 대기 없이 즉시 로드 큐 진입(첫 화면용)
 */
export default function GameThumbnail({ src, alt, priority = false }) {
  const [phase, setPhase] = useState(() => initialPhase(src, priority))
  const [attempt, setAttempt] = useState(0) // 0=원본, 1=캐시버스트 재요청

  const wrapRef = useRef(null)
  const retryRef = useRef(0)
  const aliveRef = useRef(true)
  const holdingRef = useRef(false) // 슬롯 점유 여부
  const holdTimerRef = useRef(null) // 슬롯 점유 상한 타이머
  const cancelAcquireRef = useRef(null) // 큐 대기 취소 함수

  const release = useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current)
      holdTimerRef.current = null
    }
    if (holdingRef.current) {
      holdingRef.current = false
      releaseSlot()
    }
  }, [])

  // src 변경 시 전체 초기화
  useEffect(() => {
    if (cancelAcquireRef.current) {
      cancelAcquireRef.current()
      cancelAcquireRef.current = null
    }
    release()
    retryRef.current = 0
    setAttempt(0)
    setPhase(initialPhase(src, priority))
  }, [src, priority, release])

  // 언마운트 정리 — 큐 대기 취소 + 슬롯 반납(진행 중 다운로드는 브라우저가 취소)
  useEffect(() => {
    aliveRef.current = true
    return () => {
      aliveRef.current = false
      if (cancelAcquireRef.current) cancelAcquireRef.current()
      release()
    }
  }, [release])

  // [phase=view] 뷰포트 진입 감지 → slot 으로. IO 미지원이면 바로 slot.
  useEffect(() => {
    if (phase !== 'view') return
    const el = wrapRef.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setPhase('slot')
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          io.disconnect()
          setPhase('slot')
        }
      },
      { rootMargin: PREFETCH_MARGIN }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [phase])

  // [phase=slot] 로드 슬롯 확보 → loading 으로
  useEffect(() => {
    if (phase !== 'slot') return
    cancelAcquireRef.current = acquireSlot(() => {
      cancelAcquireRef.current = null
      if (!aliveRef.current) {
        releaseSlot()
        return
      }
      holdingRef.current = true
      // 상한 시간이 지나면 슬롯만 반납해 다음 썸네일 진행(이미지는 계속 로딩).
      holdTimerRef.current = setTimeout(release, SLOT_HOLD_MS)
      setPhase('loading')
    })
    return () => {
      if (cancelAcquireRef.current) {
        cancelAcquireRef.current()
        cancelAcquireRef.current = null
      }
    }
  }, [phase, release])

  const handleLoad = () => {
    release()
    setPhase('loaded')
  }

  const handleError = () => {
    release()
    if (retryRef.current < MAX_RETRY) {
      retryRef.current += 1
      setAttempt(retryRef.current)
      setPhase('slot') // 재시도도 큐를 거쳐 순차 로드(캐시버스트)
      return
    }
    setPhase('error')
  }

  if (phase === 'error') {
    return (
      <S.Fallback ref={wrapRef}>
        <S.FallbackIcon viewBox="0 0 24 24" aria-hidden="true">
          <path d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
        </S.FallbackIcon>
        {alt && <S.FallbackTitle>{alt}</S.FallbackTitle>}
      </S.Fallback>
    )
  }

  const showImg = phase === 'loading' || phase === 'loaded'

  return (
    <S.Wrap ref={wrapRef}>
      {showImg && (
        <S.Img
          src={attempt > 0 ? withRetryParam(src) : src}
          alt={alt || ''}
          decoding="async"
          fetchpriority={priority ? 'high' : 'auto'}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
      {phase !== 'loaded' && <S.Skeleton aria-hidden="true" />}
    </S.Wrap>
  )
}
